// Import necessary modules and components
import { ErrorMessage, Form, Formik, FormikProps } from "formik";
// Import MobX observer for state management
import { observer } from "mobx-react";
// Import React and hooks for component functionality
import React, { useCallback, useEffect, useRef, useState } from "react";
// Import Bootstrap components for UI
import { Nav, Tab } from "react-bootstrap";
// Import FormattedMessage for internationalization
import { FormattedMessage } from "react-intl";
// Import booking status enum constants
import bookingStatusEnum from "../../../../constants/enums/booking-status-enum";
// Import booking type enum constants
import { BookingTypeEnum } from "../../../../constants/enums/booking-type-enum";
// Import cancellation type enum constants
import { CancellationTypeEnum } from "../../../../constants/enums/cancellation-type-enum";
// Import customer type enum constants
import { CustomerTypeEnum } from "../../../../constants/enums/customer-type-enum";
// Import payment type enum constants
import { paymentType } from "../../../../constants/enums/payment-type-enum";
// Import registered by enum constants
import { RegisteredByEnum } from "../../../../constants/enums/registered-by-enum";
// Import service type enum constants
import { serviceTypeEnum } from "../../../../constants/enums/service-type-enum";
// Import store context for state management
import { useStore } from "../../../../contexts/store-provider";
// Import helper to get store ID from local storage
import {
  getBusinessTypeIdFromLocalStorage,
  getHeadquarterIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../../../helpers/localstorage-helper";
// Import booking form values and step indicator props interfaces
import {
  BookingFormValues,
  StepIndicatorProps,
} from "../../../../models/forms/IAddBookingManagement";
// Import validation schema for reservation form
import { ReservationFormExistingCustomerSchema } from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
// Import form loader component for loading state
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
// Import formatMessage for translations
import { formatMessage } from "../../../../translations/format-message";
// Import HOC for customer authorization popup
import AuthorizeCustomerPopupHOC from "../../../booking-management/hoc/authorize-customer-popup-hoc";
// Import CSS for the component
import {
  formatNumberWithCommas,
  stringJpRegex,
  stringJpRegexValidate,
} from "../../../../constants/constants";
import { toHalfWidth } from "../../../../helpers/validate-helper";
import Label from "../../../../shared-components/label/label";
import "./add-reservation-form-component.css";
import userTypeEnum from "../../../../constants/enums/user-type-enum";
import toast from "react-hot-toast";
import { formatTimeDisplay } from "../../../../helpers/date-time-helper";
import ZeroDurationTreatmentPopup from "../../../../shared-components/popup/confirm/zero-duration-treatment";
import ResetPasswordWindowPopupHOC from "../../../../shared-components/popup/change-password/reset-password-window-popup-hoc";
import moment from "moment";
import PhoneNumberInput from "../../../../shared-components/input/phone-number-input";

// Define props interface for the component
interface IProps {
  initialValues: any; // Initial form values
  staff: any; // Staff data
  time: any; // Time data
  date: any; // Date data
  reservationSettingDetail: any;
  onModalClosed: () => void;
  storeId: number; // Store ID
}

// Define AddReservationFormComponent as a functional component
const AddReservationFormComponent: React.FC = (props: any) => {
  // State to track the current step in the form
  const [currentStep, setCurrentStep] = useState<number>(1);

  // Destructure stores from the context
  const {
    masterManagementStore,
    categoryManagementStore,
    bookingManagementStore,
    storeManagementStore,
    customerManagementStore,
  } = useStore();

  // Destructure customer reservation list state and reset function
  const { customerReservationListState, resetCustomerReservationList } =
    customerManagementStore;

  // Destructure category management store functions and states
  const { allCategoryState, GetAllCategoriesServiceReservation } =
    categoryManagementStore;

  // Destructure booking management store functions and states
  const {
    addUpdateAppointmentState,
    AddAppointmentService,
    AddWalkInCustomerAppointmentService,
    getAccordionDataById,
    resetBookingStore,
    resetAppointmentState,
    getTreatmentOptionMenuListState,
  } = bookingManagementStore;

  // Destructure store management store functions and states
  const { GetStoreManagementByIdService, storeDetail, storeState } =
    storeManagementStore;

  // Destructure master management store states
  const {
    GetAllStoreOptionService,
    GetAllTimeService,
    getAllTaxOptions,
    GetAllTaxOptionService,
    staffOptionsState,
    storeOptionsState,
    taxSettingState,
    timeSettingState,
    getAllTimeOptions,
    getAllStaffOptions,
    consumptioTaxSetting,
    getByIdConsumptionTaxState,
  } = masterManagementStore;

  // State to track the selected store ID
  const [storeIdSearch, setStoreIdSearch] = useState<number>(props.storeId);

  // State to store booking details
  const [bookingDetails, setBookingDetails] = useState<any>();

  // Ref to access Formik instance
  const formikRef = useRef<FormikProps<any> | null>(null);

  // Map selected services to extract detailed information
  const selectedServicesInfo =
    formikRef?.current?.values?.selectedServices?.map((service: any) => {
      const TreatmentData = getAccordionDataById(Number(service.TreatmentId));

      // Return null if TreatmentData is not available
      if (!TreatmentData) {
        return null;
      }

      // Now it's safe to use TreatmentData.Time
      const timeOption = getAllTimeOptions?.find(
        (time) => time.id === TreatmentData.Time
      );
      const getTime = timeOption?.value ? Number(timeOption.value) : 0;

      return {
        Name: TreatmentData.TreatmentName, // Extract treatment name
        FeeExcludingTax: TreatmentData.FeeExcludingTax, // Extract fee excluding tax
        FeeIncludingTax: TreatmentData.FeeIncludingTax, // Extract fee including tax
        Tax: TreatmentData.TaxId, // Extract tax ID
        TaxAmount: TreatmentData.TaxAmount, // Extract tax amount
        Time: getTime, // Extract time value
        TreatmentData: TreatmentData, // Store treatment data
        OptionIds: service.OptionIds, // Store selected option IDs
      };
    });

  // Calculate total duration of selected services
  const totalDuration = () => {
    let totalTime = 0;
    selectedServicesInfo?.map((service: any) => {
      totalTime += service?.Time ? service.Time : 0; // Add service time
      service?.OptionIds?.map((optionId: any) => {
        const option =
          service?.TreatmentData?.optionVm?.length > 0
            ? service.TreatmentData?.optionVm?.find(
                (op: any) => op.OptionId == optionId
              )
            : null;
        totalTime += option.TimeValue; // Add option time value
      });
    });
    return totalTime;
  };

    const totalDurationWithZeroTimeTreatmentCheck = () => {
    let totalTime = 0;
    const selectedServicesInfo =
      formikRef?.current?.values?.selectedServices?.map((service: any) => {
        const TreatmentData = getAccordionDataById(Number(service.TreatmentId));

        // Return null if TreatmentData is not available
        if (!TreatmentData) {
          return null;
        }

        // Now it's safe to use TreatmentData.Time
        const timeOption = getAllTimeOptions?.find(
          (time) => time.id === TreatmentData.Time
        );
        const getTime = timeOption?.value ? Number(timeOption.value) : 0;

        return {
          Name: TreatmentData.TreatmentName,
          FeeExcludingTax: TreatmentData.FeeExcludingTax,
          FeeIncludingTax: TreatmentData.FeeIncludingTax,
          Tax: TreatmentData.TaxId,
          TaxAmount: TreatmentData.TaxAmount,
          Time: getTime,
          TreatmentData: TreatmentData,
          OptionIds: service.OptionIds,
        };
      });
    selectedServicesInfo?.map((service:any) => {
      totalTime += service?.Time ? service.Time : 0;
      service?.OptionIds?.map((optionId: any) => {
        const option =
          service?.TreatmentData?.optionVm?.length > 0
            ? service.TreatmentData?.optionVm?.find(
                (op: any) => op.OptionId == optionId
              )
            : null;
        totalTime += option.TimeValue;
      });
    });
    return totalTime;
  };


  // Calculate total amount of selected services
  const totalAmount = () => {
    let totalFee = 0;
    selectedServicesInfo?.map((service: any) => {
      totalFee += service?.FeeIncludingTax; // Add service fee including tax
      service?.OptionIds?.map((optionId: any) => {
        const option =
          service?.TreatmentData?.optionVm?.length > 0
            ? service.TreatmentData?.optionVm?.find(
                (op: any) => op.OptionId == optionId
              )
            : null;
        totalFee += option.FeeIncludingTax; // Add option fee including tax
      });
    });
    return totalFee;
  };

  // Calculate total tax amount of selected services
  const totalTaxAmount = () => {
    let totalTax = 0;
    selectedServicesInfo?.map((service: any) => {
      totalTax += service?.TaxAmount; // Add service tax amount
      service?.OptionIds?.map((optionId: any) => {
        const option =
          service?.TreatmentData?.optionVm?.length > 0
            ? service.TreatmentData?.optionVm?.find(
                (op: any) => op.OptionId == optionId
              )
            : null;
        totalTax += option.TaxAmount; // Add option tax amount
      });
    });
    return totalTax;
  };

  const totalNominationFee = (staffId: number) => {
    let nominationFee = 0;
    const staff = getAllStaffOptions.find((s) => s.id === staffId);
    if (staff && staff.data?.NominationFee) {
      nominationFee += staff.data.NominationFee || 0; // Add nomination fee if applicable
    }
    return nominationFee;
  };

  // Get tax value by tax ID
  const getTaxValue = (taxId: number) => {
    const taxOption = getAllTaxOptions?.find((taxes) => taxes?.id == taxId);
    if (taxOption) {
      return taxOption.value; // Return tax value
    } else {
      return null; // Return null if not found
    }
  };

  // Get time value by time ID
  const getTimeValue = (timeId: number) => {
    const timeOption = getAllTimeOptions?.find((time) => time?.id == timeId);
    if (timeOption) {
      return timeOption.value; // Return time value
    } else {
      return null; // Return null if not found
    }
  };

  // Reset appointment and customer reservation states on component mount
  useEffect(() => {
    resetAppointmentState();
    resetCustomerReservationList();
    setCurrentStep(1); // Reset to step 1
    formikRef.current?.resetForm(); // Reset form
    GetStoreManagementByIdService(props.storeId); // Fetch store management data
    GetAllStoreOptionService(); // Fetch all store options
  }, [GetAllStoreOptionService]);

  // Reset states and fetch categories, time, and tax options on component mount
  useEffect(() => {
    resetAppointmentState();
    resetCustomerReservationList();
    formikRef.current?.resetForm(); // Reset form
    setCurrentStep(1); // Reset to step 1
    GetAllCategoriesServiceReservation(getBusinessTypeIdFromLocalStorage()); // Fetch all categories
    GetAllTimeService(); // Fetch all time options
    GetAllTaxOptionService(); // Fetch all tax options
  }, []);

  const isStepAccessible = (
    values: BookingFormValues,
    step: number
  ): boolean => {
    // Step 1 is always accessible
    if (step === 1) return true;

    // Step 2 is accessible if services are selected
    if (step === 2) return values.selectedServices.length > 0;
    return false;
  };

  const handleSubmit = (values: any) => {
    // Process the final submission
    let businessTypeId = getBusinessTypeIdFromLocalStorage();
    let headquarterId = getHeadquarterIdFromLocalStorage();
    let customerId = Number(values?.selectedCustomer); // Extract selected customer ID
    let servicesWithCustomerID: any[] = []; // Array to store services with customer ID
    let servicesWithOutCustomerID: any[] = []; // Array to store services without customer ID
    let nominationFee = 0;//totalNominationFee(values.selectedStaff); // Calculate nomination fee
    let totalTimeForTakenForServices = totalDuration();
    const startDateTime = moment(`${props?.date} ${props.time?.Time}`, "YYYY-MM-DD HH:mm");
    const endDateTime = moment(startDateTime).add(totalTimeForTakenForServices, "minutes");

    if (!startDateTime.isSame(endDateTime, "day")) {
      toast.error(formatMessage("the_selected_service_time_exceeds_midnight"));
      return;
    }
    let consumtionTaxPercent =
      typeof consumptioTaxSetting.TaxValue === "string"
        ? parseInt(consumptioTaxSetting.TaxValue)
        : consumptioTaxSetting.TaxValue;
    let consumptionTax = 0;//(nominationFee * consumtionTaxPercent) / 100;
    let existingCustomerObj = {
      CustomerId: customerId, // Set customer ID for existing customer
      StoreId: Number(storeIdSearch), // Set store ID
      StaffPreference: 0, // Set staff preference
      RequestedStaffId: values.selectedStaff > 0 ? values.selectedStaff : null, // Set requested staff ID if selected
      AssignedStaffId: values.selectedStaff > 0 ? values.selectedStaff : null, // Assigned staff ID is null initially
      Date: `${props.date}T00:00:00.000Z`, // Set appointment date
      Time: `${props.time?.Time}:00`, // Set appointment time
      AppointmentStatus: bookingStatusEnum.Reserved, // Set appointment status
      PaymentType: paymentType.OnSitePayment, // Set payment type
      NominationFee: nominationFee, // we are setting Nomination field to false for reservation
      TotalTime: totalTimeForTakenForServices, // Calculate total duration
      TotalAmount: totalAmount() + nominationFee + Math.round(consumptionTax), // Calculate total amount
      TotalTaxes: totalTaxAmount(), // Calculate total taxes
      ConsumptionTax: Math.round(consumptionTax), // Set consumption tax
      CancelationType: CancellationTypeEnum.None, // Set cancellation type
      BookingType: BookingTypeEnum.NewVisit, // Set booking type
      ReservationMemo: "", // Set reservation memo
      PostBreakTime: getTimeValue(props.reservationSettingDetail?.IntervalTime), // Set post-break time
      Service: servicesWithCustomerID, // Assign services with customer ID
      BusinessTypeId:
        Number(businessTypeId) > 0 ? Number(businessTypeId) : null, // Set business type ID
      HeadquarterId: 
        Number(headquarterId) > 0 ? Number(headquarterId) : null, // Set business type ID
      DmStore: null
    };

    let newCustomerObj:any = {
      FirstNameKana: values?.FirstNameKana, // Set first name for new customer
      LastNameKana: values?.LastNameKana, // Set last name for new customer
      Gender: Number(values?.Gender), // Set gender for new customer
      Phone1: toHalfWidth(values?.Phone), // Set phone number for new customer
      CustomerType: CustomerTypeEnum.UnRegistered, // Set customer type
      RegisteredBy: RegisteredByEnum.Store, // Set registered by
      StoreId: storeIdSearch, // Set store ID
      StaffPreference: 0, // Set staff preference
      RequestedStaffId: values.selectedStaff > 0 ? values.selectedStaff : null, // Set requested staff ID if selected
      AssignedStaffId: values.selectedStaff > 0 ? values.selectedStaff : null, // Assigned staff ID is null initially
      Date: `${props.date}T00:00:00.000Z`, // Set appointment date
      Time: `${props.time?.Time}:00`, // Set appointment time
      AppointmentStatus: bookingStatusEnum.Reserved, // Set appointment status
      PaymentType: paymentType.OnSitePayment, // Set payment type
      NominationFee: nominationFee, // we are setting Nomination field to false for reservation
      TotalTime: totalTimeForTakenForServices, // Calculate total duration
      TotalAmount: totalAmount() + nominationFee + Math.round(consumptionTax), // Calculate total amount
      TotalTaxes: totalTaxAmount(), // Calculate total taxes
      ConsumptionTax: Math.round(consumptionTax), // Set consumption tax
      CancelationType: CancellationTypeEnum.None, // Set cancellation type
      BookingType: BookingTypeEnum.NewVisit, // Set booking type
      ReservationMemo: "", // Set reservation memo
      PostBreakTime: getTimeValue(props.reservationSettingDetail?.IntervalTime), // Set post-break time
      Service: servicesWithOutCustomerID, // Assign services without customer ID
      BusinessTypeId:
        Number(businessTypeId) > 0 ? Number(businessTypeId) : null, // Set business type ID
      HeadquarterId: 
        Number(headquarterId) > 0 ? Number(headquarterId) : null,
      DmStore: false
    };
    if(values.Email)
      newCustomerObj.Email = values.Email;

    if (values.selectedServices?.length > 0) {
      values.selectedServices?.map((service: any) => {
        const treatmentData = getAccordionDataById(service.TreatmentId); // Get treatment data by ID
        if (treatmentData) {
          servicesWithCustomerID.push({
            CustomerId: customerId, // Add customer ID to service
            ServiceId: service.TreatmentId, // Add service ID
            ParentId: treatmentData.CategoryId, // Set parent category ID
            ServiceType: serviceTypeEnum.Treatment, // Set service type
            ServiceName: treatmentData.TreatmentName, // Set service name
            Charges: treatmentData.FeeIncludingTax.toString(), // Set service charges
            TaxAmount: treatmentData.TaxAmount, // Set service charges
            Tax: getTaxValue(treatmentData.TaxId), // Get tax value
            TimeValue: getTimeValue(treatmentData.Time), // Get time value
          });
          servicesWithOutCustomerID.push({
            ServiceId: service.TreatmentId, // Add service ID
            ServiceType: serviceTypeEnum.Treatment, // Set service type
            ParentId: treatmentData.CategoryId, // Set parent category ID
            ServiceName: treatmentData.TreatmentName, // Set service name
            Charges: treatmentData.FeeIncludingTax.toString(), // Set service charges
            TaxAmount: treatmentData.TaxAmount, // Set service charges
            Tax: getTaxValue(treatmentData.TaxId), // Get tax value
            TimeValue: getTimeValue(treatmentData.Time), // Get time value
          });
          if (service.OptionIds?.length > 0) {
            service?.OptionIds?.map((id: number) => {
              const option =
                treatmentData?.optionVm?.length > 0
                  ? treatmentData?.optionVm?.find(
                      (op: any) => op.OptionId == id
                    )
                  : null; // Find option by ID
              if (option) {
                servicesWithCustomerID.push({
                  CustomerId: customerId, // Add customer ID to option
                  ServiceId: option.OptionId, // Add option ID
                  ParentId: treatmentData.Id, // Set parent treatment ID
                  ServiceType: serviceTypeEnum.Option, // Set option type
                  ServiceName: `${treatmentData.Id}_${option.OptionName}`, // Set option name
                  Charges: option.FeeIncludingTax.toString(), // Set option charges
                  TaxAmount: option.TaxAmount, // Set option charges
                  Tax: getTaxValue(option.TaxId), // Get tax value
                  TimeValue: option?.TimeValue, // Get time value
                });
                servicesWithOutCustomerID.push({
                  ServiceId: option.OptionId, // Add option ID
                  ServiceType: serviceTypeEnum.Option, // Set option type
                  ParentId: treatmentData.Id, // Set parent treatment ID
                  ServiceName: `${treatmentData.Id}_${option.OptionName}`, // Set option name
                  Charges: option.FeeIncludingTax.toString(), // Set option charges
                  TaxAmount: option.TaxAmount, // Set option charges
                  Tax: getTaxValue(option.TaxId), // Get tax value
                  TimeValue: option?.TimeValue, // Get time value
                });
              }
            });
          }
        }
      });
    }

    if (values?.IsNewCustomer == true) {
      setBookingDetails(newCustomerObj); // Set booking details for new customer
      AddWalkInCustomerAppointmentService(newCustomerObj); // Add walk-in customer appointment
    } else {
      setBookingDetails(existingCustomerObj); // Set booking details for existing customer
      AddAppointmentService(existingCustomerObj); // Add appointment for existing customer
    }
  };

  const validateOptionMandatoryForTreatment = (selectedServices: any[]) =>{
    const treatmentsMissingOptions: string[] = [];
    const allSelectedOptionIds = selectedServices.flatMap((service: any) => service.OptionIds || []);

    for (const service of selectedServices) {
      const treatmentData = getAccordionDataById(service.TreatmentId);
      if (!treatmentData) continue;

      if (treatmentData.OptionMenu === 0 && Array.isArray(treatmentData.optionVm) && treatmentData.optionVm.length > 0) {
        const treatmentOptionIds = treatmentData?.optionVm.map((opt:any) => opt.OptionId);
        const isOptionSelectedAnywhere = treatmentOptionIds.some((optId: number) =>
          allSelectedOptionIds.includes(optId)
        );

        if (!isOptionSelectedAnywhere) {
          treatmentsMissingOptions.push(treatmentData.TreatmentName);
        }
      }
    }

    if (treatmentsMissingOptions.length > 0) {
      toast.error(
        `${formatMessage('option_is_mandatory_for')}: ${treatmentsMissingOptions.join(', ')}`,
      );
      return false; // Prevent submit
    }
    return true;
  }

  return (
    <>
      {/* Display loader while data is being fetched */}
      <FormLoader
        loading={
          staffOptionsState.inProgress ||
          taxSettingState.inProgress ||
          storeOptionsState.inProgress ||
          timeSettingState.inProgress ||
          storeState.inProgress ||
          getTreatmentOptionMenuListState.inProgress ||
          allCategoryState.inProgress ||
          addUpdateAppointmentState.inProgress ||
          customerReservationListState.inProgress ||
          getByIdConsumptionTaxState.inProgress
        }
      />

      {props.showResetPasswordPopup && (
              <ZeroDurationTreatmentPopup
                modalClosed={() => {
                  props.resetPasswordToggleHandler();
                }}
                title={formatMessage("reservation_management")}
                message={formatMessage("zero_minute_treatment_alert_message")}
                isLoading={false}
              />
            )}

      {storeIdSearch > 0 ? (
        <Formik
          initialValues={props.initialValues} // Set initial form values
          validationSchema={ReservationFormExistingCustomerSchema} // Apply validation schema
          onSubmit={handleSubmit} // Handle form submission
          innerRef={formikRef} // Reference to Formik instance
          validateOnBlur={false} // Disable auto-validation on blur
          validateOnChange={false} // Disable auto-validation on change
        >
          {(formikProps) => (
            <Form className="container-fluid sidebar_booking">
              {/* Render step indicator */}
              <StepIndicator
                currentStep={currentStep}
                isStepAccessible={(step) =>
                  isStepAccessible(formikProps.values, step)
                }
              />
              <div className="booking-content">
                {/* Render service selection for step 1 */}
                {currentStep === 1 && (
                  <ServiceSelection formikProps={formikProps} />
                )}

                {/* Render customer selection for step 2 */}
                {currentStep === 2 && (
                  <>
                    <ChooseCustomer
                      formikProps={formikProps}
                      storeId={storeIdSearch}
                    />
                  </>
                )}
              </div>

              {currentStep < 3 && (
                <div className="d-flex justify-content-between booking-content">
                  {/* Render back button for steps other than the first */}
                  {currentStep !== 1 ? (
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-2 px-md-4"
                      onClick={() => {
                        formikRef.current?.setFieldValue("FirstNameKana", ""); // Reset first name
                        formikRef.current?.setFieldValue("LastNameKana", ""); // Reset last name
                        formikRef.current?.setFieldValue("Email", ""); // Reset email
                        formikRef.current?.setFieldValue("Phone", ""); // Reset phone
                        formikRef.current?.setFieldValue(
                          "IsNewCustomer",
                          false
                        ); // Reset new customer flag
                        formikRef.current?.setFieldValue(
                          "selectedCustomer",
                          "-1"
                        ); // Reset selected customer
                        resetCustomerReservationList(); // Reset customer reservation list
                        setCurrentStep(Math.max(currentStep - 1, 1)); // Navigate to the previous step
                      }}
                    >
                      <FormattedMessage id="return" />
                    </button>
                  ) : (
                    <div></div>
                  )}

                  {/* Render confirm button for the last step */}
                  {currentStep == 2 ? (
                    <button
                      type="button"
                      className="btn btn-info btn-lg px-4 px-md-5"
                      onClick={async () => {
                        const isValid = await formikProps
                          .validateForm()
                          .then((errors) => {
                            // Touch all fields with errors
                            Object.keys(errors).forEach((field) => {
                              formikProps.setFieldTouched(field, true, false);
                            });
                            return Object.keys(errors).length === 0;
                          });

                        if (isValid) {
                          formikProps.handleSubmit(); // Submit the form
                        }
                      }}
                    >
                      <FormattedMessage id="confirm_your_booking" />
                    </button>
                  ) : (
                    // Render next button for intermediate steps
                    <button
                      type="button"
                      className="btn btn-dark px-2 px-md-4"
                      onClick={() => {
                        let canProceed = false;

                        if (currentStep === 1) {

                          if (
                                  Number(
                                    totalDurationWithZeroTimeTreatmentCheck()
                                  ) == 0
                                ) {
                                  props.resetPasswordToggleHandler();
                                  return;
                                }

                          formikProps.validateField("selectedServices"); // Validate selected services
                          const isValidatedSuccessfully = validateOptionMandatoryForTreatment(formikProps.values.selectedServices);
                          if(!isValidatedSuccessfully) return;
                          canProceed =
                            formikProps.values.selectedServices.length > 0; // Check if services are selected
                        }

                        if (canProceed) {
                          setCurrentStep(Math.min(currentStep + 1, 3)); // Navigate to the next step
                        } else {
                          // Show validation messages by touching the fields
                          if (currentStep === 1) {
                            formikProps.setFieldTouched(
                              "selectedServices",
                              true,
                              true
                            );
                          }
                        }
                      }}
                    >
                      <FormattedMessage id="next" />
                    </button>
                  )}
                </div>
              )}
            </Form>
          )}
        </Formik>
      ) : (
        // Display message if no store is selected
        <div className="container-fluid sidebar_booking">
          <h1>
            <FormattedMessage id="please_select_store" />
          </h1>
        </div>
      )}
    </>
  );
};

const StepIndicator: React.FC<StepIndicatorProps> = observer(
  ({ currentStep, isStepAccessible }) => {
    // Define the steps with their respective numbers and titles
    const steps = [
      { number: 1, title: formatMessage("choose_a_menu") },
      { number: 2, title: formatMessage("choose_a_customer") },
    ];

    return <></>; // Render an empty fragment (to be implemented later)
  }
);

const ServiceSelection: React.FC<{
  formikProps: FormikProps<any>;
}> = observer(({ formikProps }) => {
  const { values, setFieldValue } = formikProps; // Destructure values and setFieldValue from formikProps
  const {
    categoryManagementStore,
    bookingManagementStore,
    masterManagementStore,
  } = useStore(); // Destructure stores from the context
  const { allCategoryBookingManagement } = categoryManagementStore; // Get all categories for booking management
  const { getAllTimeOptions } = masterManagementStore; // Get all time options from master management store
  const { GetTreatmentOptionMenuListByCatIdService, getData } =
    bookingManagementStore; // Get services and data fetching functions from booking management store

  // State to track open/closed status for any node by its id.
  // State to track open/closed status for nodes by their IDs
  const [openNodes, setOpenNodes] = useState<{ [key: string]: boolean }>({});

  // Toggle the open/close state for a given node ID
  const toggleNode = (nodeId: string) => {
    setOpenNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId], // Flip the current state of the node
    }));
  };

  const handleOptionServiceSelect = (treatmentId: number, optionId: number) => {
    const selectedServices: any[] = [...values.selectedServices];
    const treatmentObj = selectedServices?.find(
      (item) => item.TreatmentId === treatmentId
    );
    let duplicateOptionId = selectedServices.find((item) =>
      item.OptionIds?.includes(optionId)
    );
    if (treatmentObj) {
      // Check if the optionId already exists in the optionIds array
      const optionIndex = treatmentObj.OptionIds?.indexOf(optionId);
      if (duplicateOptionId && duplicateOptionId?.TreatmentId !== treatmentId) {
        toast.error(formatMessage("option_already_selected"));
      } else {
        if (optionIndex === -1) {
          treatmentObj.OptionIds.push(optionId);
        } else {
          treatmentObj.OptionIds.splice(optionIndex, 1);
        }
      }
    } else {
      selectedServices.push({
        TreatmentId: treatmentId,
        OptionIds: duplicateOptionId ? [] : [optionId],
      });
    }
    setFieldValue("selectedServices", selectedServices);
  };

  // Function to handle selection/deselection of a treatment service
  const handleTreatmentServiceSelect = (treatmentId: number) => {
    const selectedServices: any[] = [...values.selectedServices]; // Clone the selected services array
    const index = selectedServices.findIndex(
      (service: any) => service.TreatmentId == treatmentId // Find the index of the treatment in the selected services
    );
    if (index === -1) {
      selectedServices.push({ TreatmentId: treatmentId, OptionIds: [] }); // Add the treatment if not already selected
    } else {
      selectedServices.splice(index, 1); // Remove the treatment if already selected
    }
    setFieldValue("selectedServices", selectedServices); // Update the selected services in the form state
  };

  // Recursive function to render nested children (services)
  const renderNestedServices = (
    subCategories: any[],
    parentPath: string = ""
  ) => {
    return (
      <div className="px-1 mt-2">
        {subCategories?.map((subCategory: any) => {
          const nodeKey = parentPath
            ? `${parentPath}-${subCategory.id}`
            : `${subCategory.id}`; // Generate unique key for each subcategory
          return (
            <div key={nodeKey}>
              <button
                type="button"
                className="accordian_btn d-flex justify-content-between align-items-center text-start"
                onClick={() => {
                  let userType = getUserTypeFromLocalStorage();
                  toggleNode(nodeKey); // Toggle the visibility of the subcategory
                  GetTreatmentOptionMenuListByCatIdService(
                    subCategory.id,
                    userTypeEnum[userTypeEnum.Store] !== userType
                      ? values?.selectedStaff
                      : null
                  ); // Fetch treatment options for the subcategory
                }}
              >
                {subCategory.title}
                {openNodes[nodeKey] ? (
                  <svg
                    fill="currentColor"
                    height="16"
                    viewBox="0 0 367.676 367.676"
                    width="16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g>
                      <g>
                        <path d="m344.696 287.246c-5.881 0-11.763-2.243-16.249-6.73l-144.61-144.609-144.608 144.609c-8.973 8.975-23.524 8.975-32.498 0-8.974-8.974-8.974-23.524 0-32.498l160.857-160.859c4.31-4.309 10.154-6.731 16.249-6.731 6.094 0 11.94 2.422 16.25 6.731l160.858 160.858c8.974 8.974 8.974 23.524 0 32.498-4.486 4.488-10.368 6.731-16.249 6.731z"></path>
                      </g>
                    </g>
                  </svg>
                ) : (
                  <svg
                    fill="currentColor"
                    height="16"
                    viewBox="0 0 367.676 367.676"
                    width="16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g>
                      <g>
                        <path d="m183.837 287.247c-6.095 0-11.939-2.422-16.249-6.731l-160.857-160.859c-8.974-8.975-8.974-23.524 0-32.498 8.975-8.974 23.525-8.973 32.498 0l144.609 144.609 144.609-144.609c8.973-8.974 23.525-8.974 32.498 0 8.974 8.974 8.974 23.524 0 32.498l-160.858 160.859c-4.31 4.309-10.155 6.731-16.25 6.731z"></path>
                      </g>
                    </g>
                  </svg>
                )}
              </button>
              {openNodes[nodeKey] && (
                <div className="px-1 mt-2">
                  {getData(subCategory.id)?.length > 0 ? (
                    getData(subCategory.id)?.map((treatment: any) => {
                      const treatmentKey = parentPath
                        ? `${parentPath}-${subCategory.id}-${treatment.Id}`
                        : `${subCategory.id}-${treatment.Id}`; // Generate unique key for each treatment
                      return (
                        <div key={treatmentKey}>
                          <div
                            className={`service-item mb-2 ${
                              openNodes[treatment.Id] ? "active" : "" // Highlight active treatment
                            }
                          ${
                            values.selectedServices.some(
                              (service: any) =>
                                service.TreatmentId == Number(treatment.Id) // Check if treatment is selected
                            )
                              ? "selected"
                              : ""
                          }`}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="treatment_selection_name">
                                <h4>{treatment.TreatmentName}</h4>{" "}
                                {/* Display treatment name */}
                                <div className="small text-muted">
                                  <i className="bi bi-currency-yen me-1"></i>
                                  {formatNumberWithCommas(treatment.FeeIncludingTax)} ·{" "}
                                  {
                                    formatTimeDisplay(Number(getAllTimeOptions?.find(
                                      (time) => time.id === treatment.Time // Find time value for the treatment
                                    )?.value ?? 0))
                                  }{" "}
                                </div>
                                <div className="small text-muted">
                                  {treatment.Description}{" "}
                                  {/* Display treatment description */}
                                </div>
                              </div>
                              {values.selectedServices.some(
                                (service: any) =>
                                  service.TreatmentId == Number(treatment.Id) // Check if treatment is selected
                              ) ? (
                                <div className="text-end">
                                  <span className="bg-primary text-white py-1 px-1 px-md-2 fs-sm rounded me-2">
                                    <svg
                                      className="me-1"
                                      height="12"
                                      viewBox="0 -65 512 512"
                                      width="12"
                                      fill="currentColor"
                                    >
                                      <path d="m444.175781 0-260.871093 242.011719-110.324219-117.734375-72.980469 68.386718 178.234375 190.207032 333.765625-309.351563zm0 0"></path>
                                    </svg>
                                    <FormattedMessage id="menu_added" />
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={(e) => {
                                      toggleNode(treatmentKey); // Toggle treatment visibility
                                      e.stopPropagation();
                                      handleTreatmentServiceSelect(
                                        Number(treatment.Id) // Deselect treatment
                                      );
                                    }}
                                  >
                                    <svg
                                      x="0px"
                                      y="0px"
                                      viewBox="0 0 512 512"
                                      fill="currentColor"
                                      width="14"
                                    >
                                      <g>
                                        <g>
                                          <path
                                            d="M436,60h-75V45c0-24.813-20.187-45-45-45H196c-24.813,0-45,20.187-45,45v15H76c-24.813,0-45,20.187-45,45
          c0,19.928,13.025,36.861,31.005,42.761L88.76,470.736C90.687,493.875,110.385,512,133.604,512h244.792
          c23.22,0,42.918-18.125,44.846-41.271l26.753-322.969C467.975,141.861,481,124.928,481,105C481,80.187,460.813,60,436,60z M181,45
          c0-8.271,6.729-15,15-15h120c8.271,0,15,6.729,15,15v15H181V45z M393.344,468.246c-0.643,7.712-7.208,13.754-14.948,13.754
          H133.604c-7.739,0-14.305-6.042-14.946-13.747L92.294,150h327.412L393.344,468.246z M436,120H76c-8.271,0-15-6.729-15-15
          s6.729-15,15-15h360c8.271,0,15,6.729,15,15S444.271,120,436,120z"
                                          ></path>
                                        </g>
                                      </g>
                                      <g>
                                        <g>
                                          <path
                                            d="M195.971,436.071l-15-242c-0.513-8.269-7.67-14.558-15.899-14.043c-8.269,0.513-14.556,7.631-14.044,15.899l15,242.001
          c0.493,7.953,7.097,14.072,14.957,14.072C189.672,452,196.504,444.684,195.971,436.071z"
                                          ></path>
                                        </g>
                                      </g>
                                      <g>
                                        <g>
                                          <path d="M256,180c-8.284,0-15,6.716-15,15v242c0,8.284,6.716,15,15,15s15-6.716,15-15V195C271,186.716,264.284,180,256,180z"></path>
                                        </g>
                                      </g>
                                      <g>
                                        <g>
                                          <path
                                            d="M346.927,180.029c-8.25-0.513-15.387,5.774-15.899,14.043l-15,242c-0.511,8.268,5.776,15.386,14.044,15.899
          c8.273,0.512,15.387-5.778,15.899-14.043l15-242C361.483,187.659,355.196,180.541,346.927,180.029z"
                                          ></path>
                                        </g>
                                      </g>
                                    </svg>
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={(e) => {
                                    toggleNode(treatmentKey); // Toggle treatment visibility
                                    e.stopPropagation();
                                    handleTreatmentServiceSelect(
                                      Number(treatment.Id) // Select treatment
                                    );
                                  }}
                                >
                                  + <FormattedMessage id="add_a_menu" />
                                </button>
                              )}
                            </div>
                            {values?.selectedServices?.some(
                              (service: any) =>
                                service.TreatmentId === Number(treatment.Id)
                            ) &&
                              treatment.optionVm?.length > 0 && (
                                <div className="options-menu">
                                  {treatment.optionVm?.map(
                                    (option: any, index: number) => (
                                      <React.Fragment>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                          <div>{option.OptionName}
                                              <span className="badge bg-primary rounded ms-2">
                                                <FormattedMessage id="option" />
                                              </span>
                                            </div>{" "}
                                          {/* Display option name */}
                                          {values.selectedServices?.some(
                                            (item: any) =>
                                              item.TreatmentId ===
                                              Number(treatment.Id) &&
                                              item.OptionIds?.includes(
                                                Number(option.OptionId)
                                              ) // Check if option is selected
                                          ) ? (
                                              <button
                                                type="button"
                                                key={option.OptionId}
                                                className="btn btn-primary btn-sm"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleOptionServiceSelect(
                                                    Number(treatment.Id), // Select treatment option
                                                    Number(option.OptionId)
                                                  );
                                                }}
                                              >
                                                <span>
                                                  <i className="bi bi-check-lg me-1"></i>
                                                  <FormattedMessage id="options_selected" />
                                                </span>
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                key={option.OptionId}
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleOptionServiceSelect(
                                                    Number(treatment.Id), // Select treatment option
                                                    Number(option.OptionId)
                                                  );
                                                }}
                                              >
                                                <span>
                                                  <FormattedMessage id="select_option_menu" />
                                                </span>
                                              </button>
                                            )}
                                        </div>
                                      </React.Fragment>
                                    )
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-center">
                      <FormattedMessage id="no_treatment" />
                    </p>
                  )}
                </div>
              )}
              {subCategory.children &&
                subCategory.children?.length > 0 &&
                renderNestedServices(subCategory.children, nodeKey)}{" "}
              {/* Recursively render nested subcategories */}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="service-selection">
        {/* <MenuSelectionSummary selectedServices={values.selectedServices} /> */}

        {/* Error Message */}
        <ErrorMessage
          name="selectedServices"
          component="div"
          className="text-danger mb-2"
        />

        {/* Category Dropdowns */}
        <div className="choose-menu-section mb-4 p-2">
          {allCategoryBookingManagement?.length > 0 ? (
            allCategoryBookingManagement?.map((category: any) => {
              if (category.distinction == 1) {
                return (
                  <div key={category.id} className="px-1">
                    {/* Button to toggle category dropdown */}
                    <button
                      type="button"
                      className="accordian_btn d-flex justify-content-between align-items-center text-start"
                      onClick={() => {
                        let userType = getUserTypeFromLocalStorage();
                        toggleNode(`${category.id}-NCT`);
                        GetTreatmentOptionMenuListByCatIdService(
                          category.id,
                          userTypeEnum[userTypeEnum.Store] !== userType
                            ? values?.selectedStaff
                            : null
                        );
                      }}
                    >
                      {category.title}
                      {openNodes[`${category.id}-NCT`] ? (
                        <svg
                          fill="currentColor"
                          height="16"
                          viewBox="0 0 367.676 367.676"
                          width="16"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g>
                            <g>
                              <path d="m344.696 287.246c-5.881 0-11.763-2.243-16.249-6.73l-144.61-144.609-144.608 144.609c-8.973 8.975-23.524 8.975-32.498 0-8.974-8.974-8.974-23.524 0-32.498l160.857-160.859c4.31-4.309 10.154-6.731 16.249-6.731 6.094 0 11.94 2.422 16.25 6.731l160.858 160.858c8.974 8.974 8.974 23.524 0 32.498-4.486 4.488-10.368 6.731-16.249 6.731z"></path>
                            </g>
                          </g>
                        </svg>
                      ) : (
                        <svg
                          fill="currentColor"
                          height="16"
                          viewBox="0 0 367.676 367.676"
                          width="16"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g>
                            <g>
                              <path d="m183.837 287.247c-6.095 0-11.939-2.422-16.249-6.731l-160.857-160.859c-8.974-8.975-8.974-23.524 0-32.498 8.975-8.974 23.525-8.973 32.498 0l144.609 144.609 144.609-144.609c8.973-8.974 23.525-8.974 32.498 0 8.974 8.974 8.974 23.524 0 32.498l-160.858 160.859c-4.31 4.309-10.155 6.731-16.25 6.731z"></path>
                            </g>
                          </g>
                        </svg>
                      )}
                    </button>

                    {openNodes[`${category.id}-NCT`] && (
                      <div className="mt-2">
                        {getData(category.id)?.length > 0 ? (
                          getData(category.id)?.map((treatment) => {
                            const treatmentKey = category.id
                              ? `${category.id}-NCT-${treatment.Id}`
                              : `${category.id}-${treatment.Id}`;
                            return (
                              <div key={treatmentKey}>
                                <div
                                  className={`service-item mb-2 ${
                                    openNodes[treatment.Id] ? "active" : ""
                                  }
                              ${
                                values.selectedServices.some(
                                  (service: any) =>
                                    service.TreatmentId == Number(treatment.Id)
                                )
                                  ? "selected"
                                  : ""
                              }`}
                                  onClick={() => {
                                    // toggleNode(treatmentKey);
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="treatment_selection_name">
                                      <h4>{treatment.TreatmentName}</h4>
                                      <div className="small text-muted">
                                        <i className="bi bi-currency-yen me-1"></i>
                                        {formatNumberWithCommas(treatment.FeeIncludingTax)} ·{" "}
                                        {
                                          formatTimeDisplay(Number(getAllTimeOptions?.find(
                                            (time) => time.id === treatment.Time
                                          )?.value ?? 0))
                                        }
                                      </div>
                                      <div className="small text-muted">
                                        {treatment.Description}
                                      </div>
                                    </div>

                                    {values.selectedServices.some(
                                      (service: any) =>
                                        service.TreatmentId ==
                                        Number(treatment.Id)
                                    ) ? (
                                      <div className="text-end">
                                        {/* Display selected treatment */}
                                        <span className="bg-primary text-white py-1 px-1 px-md-2 fs-sm rounded me-2">
                                          <svg
                                            className="me-1"
                                            height="12"
                                            viewBox="0 -65 512 512"
                                            width="12"
                                            fill="currentColor"
                                          >
                                            <path d="m444.175781 0-260.871093 242.011719-110.324219-117.734375-72.980469 68.386718 178.234375 190.207032 333.765625-309.351563zm0 0"></path>
                                          </svg>
                                          <FormattedMessage id="menu_added" />
                                        </span>
                                        <button
                                          type="button"
                                          className="btn btn-outline-danger btn-sm"
                                          onClick={(e) => {
                                            toggleNode(treatmentKey);
                                            e.stopPropagation();
                                            handleTreatmentServiceSelect(
                                              Number(treatment.Id)
                                            );
                                          }}
                                        >
                                          <svg
                                            x="0px"
                                            y="0px"
                                            viewBox="0 0 512 512"
                                            fill="currentColor"
                                            width="14"
                                          >
                                            <g>
                                              <g>
                                                <path
                                                  d="M436,60h-75V45c0-24.813-20.187-45-45-45H196c-24.813,0-45,20.187-45,45v15H76c-24.813,0-45,20.187-45,45
        c0,19.928,13.025,36.861,31.005,42.761L88.76,470.736C90.687,493.875,110.385,512,133.604,512h244.792
        c23.22,0,42.918-18.125,44.846-41.271l26.753-322.969C467.975,141.861,481,124.928,481,105C481,80.187,460.813,60,436,60z M181,45
        c0-8.271,6.729-15,15-15h120c8.271,0,15,6.729,15,15v15H181V45z M393.344,468.246c-0.643,7.712-7.208,13.754-14.948,13.754
        H133.604c-7.739,0-14.305-6.042-14.946-13.747L92.294,150h327.412L393.344,468.246z M436,120H76c-8.271,0-15-6.729-15-15
        s6.729-15,15-15h360c8.271,0,15,6.729,15,15S444.271,120,436,120z"
                                                ></path>
                                              </g>
                                            </g>
                                            <g>
                                              <g>
                                                <path
                                                  d="M195.971,436.071l-15-242c-0.513-8.269-7.67-14.558-15.899-14.043c-8.269,0.513-14.556,7.631-14.044,15.899l15,242.001
        c0.493,7.953,7.097,14.072,14.957,14.072C189.672,452,196.504,444.684,195.971,436.071z"
                                                ></path>
                                              </g>
                                            </g>
                                            <g>
                                              <g>
                                                <path d="M256,180c-8.284,0-15,6.716-15,15v242c0,8.284,6.716,15,15,15s15-6.716,15-15V195C271,186.716,264.284,180,256,180z"></path>
                                              </g>
                                            </g>
                                            <g>
                                              <g>
                                                <path
                                                  d="M346.927,180.029c-8.25-0.513-15.387,5.774-15.899,14.043l-15,242c-0.511,8.268,5.776,15.386,14.044,15.899
        c8.273,0.512,15.387-5.778,15.899-14.043l15-242C361.483,187.659,355.196,180.541,346.927,180.029z"
                                                ></path>
                                              </g>
                                            </g>
                                          </svg>
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={(e) => {
                                          toggleNode(treatmentKey);
                                          e.stopPropagation();
                                          handleTreatmentServiceSelect(
                                            Number(treatment.Id)
                                          );
                                        }}
                                      >
                                        + <FormattedMessage id="add_a_menu" />
                                      </button>
                                    )}
                                  </div>

                                  {values?.selectedServices?.some(
                                    (service: any) =>
                                      service.TreatmentId ===
                                      Number(treatment.Id)
                                  ) &&
                                    treatment.optionVm?.length > 0 && (
                                      <div className="options-menu mb-1">
                                        {treatment.optionVm?.map(
                                          (option: any, index: number) => (
                                            <React.Fragment>
                                              <div className="d-flex justify-content-between align-items-center p-2">
                                                <div className="treatment_selection_name mb-2">
                                                  {/* <strong>{index + 1}.</strong>{" "} */}
                                                  {option.OptionName}
                                                  <span className="badge bg-primary rounded ms-2">
                                                    <FormattedMessage id="option" />
                                                  </span>
                                                </div>

                                                {values.selectedServices?.some(
                                                      (item: any) =>
                                                        item.TreatmentId ===
                                                          Number(
                                                            treatment.Id
                                                          ) &&
                                                        item.OptionIds?.includes(
                                                          Number(
                                                            option.OptionId
                                                          )
                                                        )
                                                    ) ? (
                                              <button
                                                type="button"
                                                key={option.OptionId}
                                                className="btn btn-primary btn-sm"
                                                onClick={(e) => {
                                                   e.stopPropagation();
                                                    handleOptionServiceSelect(
                                                      Number(treatment.Id),
                                                      Number(option.OptionId)
                                                    );
                                                }}
                                              >
                                                <span>
                                                  <i className="bi bi-check-lg me-1"></i>
                                                  <FormattedMessage id="options_selected" />
                                                </span>
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                key={option.OptionId}
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleOptionServiceSelect(
                                                    Number(treatment.Id), // Select treatment option
                                                    Number(option.OptionId)
                                                  );
                                                }}
                                              >
                                                <span>
                                                  <FormattedMessage id="select_option_menu" />
                                                </span>
                                              </button>
                                            )}
                                              </div>
                                            </React.Fragment>
                                          )
                                        )}
                                      </div>
                                    )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                           category.children?.length === 0 &&
                            <p className="text-center">
                              <FormattedMessage id="no_treatment" />
                            </p>
                        )}
                      </div>
                    )}

                    {/* Render nested children for this category */}
                    {openNodes[`${category.id}-NCT`] &&
                      category.children &&
                      renderNestedServices(category.children, category.id)}
                  </div>
                );
              }
            })
          ) : (
            <div className="text-center">
              <FormattedMessage id="NoCategory" />
            </div>
          )}
        </div>
      </div>
    </>
  );
});

// Component to choose between existing or new customers
const ChooseCustomer: React.FC<{
  formikProps: FormikProps<any>;
  storeId: number;
}> = observer(({ formikProps, storeId }) => {
  const { values, setFieldValue } = formikProps;
  const { customerManagementStore } = useStore();
  const [currentTab, setCurrentTab] = useState<number>(0); // State for active tab
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(
    values?.selectedCustomer
  );
  const { GetCustomerListServiceReservation, customerReservationListState } =
    customerManagementStore;

  useEffect(() => {
    if (selectedCustomerId !== "") {
      setFieldValue("selectedCustomer", selectedCustomerId); // Update selected customer in formik
    }
  }, [selectedCustomerId]);

  const downLoaderRef = useRef<HTMLDivElement | null>(null); // Ref for infinite scroll loader
  const [hasMore, setHasMore] = useState(true); // State to track if more data is available
  const [pageSize, setPageSize] = useState<number>(10); // Page size for pagination
  const [email, setEmail] = useState(""); // State for email filter
  const [phone, setPhone] = useState(""); // State for phone filter
  const [nameKana, setNameKana] = useState(""); // State for name filter
  const [customerCode, setCustomerCode] = useState(""); // State for customer code filter
  const [nameKanaError, setNameKanaError] = useState(""); // State for name filter
  const [otherStores, setOtherStores] = useState(false); // State for other stores filter
  const [customers, setCustomers] = useState<any[]>([]); // State for customer list

  const callCustomerListReservationFilter = (
    offset: number = customers?.length
  ) => {
    setCustomers([]); // Reset customer list
    setHasMore(true); // Reset hasMore flag
    GetCustomerListServiceReservation(
      toHalfWidth(nameKana),
      email,
      offset,
      pageSize,
      toHalfWidth(phone),
      storeId,
      otherStores,
      setHasMore,
      setPageSize,
      setCustomers,
      customerCode
    );
  };

  const fetchData = useCallback(async () => {
    if (customerReservationListState.inProgress || !hasMore) return; // Prevent fetching if already in progress or no more data
    await GetCustomerListServiceReservation(
      toHalfWidth(nameKana),
      email,
      customers.length,
      pageSize,
      toHalfWidth(phone),
      storeId,
      otherStores,
      setHasMore,
      setPageSize,
      setCustomers,
      customerCode
    );
  }, [customers, hasMore, customerReservationListState.inProgress]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !customerReservationListState.inProgress &&
          hasMore
        ) {
          fetchData(); // Fetch more data when loader is visible
        }
      },
      { threshold: 1.0 }
    );

    const currentLoader = downLoaderRef.current;
    if (currentLoader) observer.observe(currentLoader); // Observe loader
    return () => {
      if (currentLoader) observer.unobserve(currentLoader); // Unobserve loader on cleanup
    };
  }, [customers]);

  return (
    <div className="container-fluid">
      <Tab.Container
        id="customer-tabs"
        defaultActiveKey={currentTab}
        onSelect={(e: any) => {
          setCurrentTab(Number(e)); // Update active tab
          if (e == "1") {
            setFieldValue("IsNewCustomer", true); // Set new customer flag
          } else {
            setFieldValue("IsNewCustomer", false); // Set existing customer flag
          }
        }}
      >
        <Nav variant="tabs">
          <Nav.Item>
            <Nav.Link eventKey={0} className="d-flex align-items-center">
              <span className="me-1">👤</span>{" "}
              <FormattedMessage id="existing" />
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={1} className="d-flex align-items-center">
              <span className="me-1">👤+</span>
              <FormattedMessage id="new" />
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Existing Customers Tab Content */}
          <Tab.Pane eventKey={0}>
            <div className="p-3">
              <div className="row g-2">
                <div className="col-md-6 form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("name_kana")}
                    value={nameKana}
                    onChange={(e) => {
                      // if (stringJpRegex.test(e.target.value)) {
                      setNameKana(e.target.value);
                      setNameKanaError("");
                      // } else {
                      //   e.preventDefault();
                      // }
                    }} // Update name filter
                    onBlur={() => {
                      // if (!stringJpRegexValidate.test(nameKana)) {
                      //   setNameKanaError("only_kana_are_allowed");
                      // }
                    }}
                  />
                  {nameKanaError && (
                    <span className="text-danger errorMessage">
                      {formatMessage(nameKanaError)}
                    </span>
                  )}
                </div>
                <div className="col-md-6 form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("contact_number")}
                    value={phone}
                    onChange={(e) => 
                      {
                        const value = e.target.value;
                        setPhone(value)
                      }
                    } // Update phone filter
                  />
                </div>
                <div className="col-md-6 form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("diana_code")}
                    value={customerCode}
                    onChange={(e) => {
                      setCustomerCode(e.target.value);
                    }} // Update name filter
                  />
                </div>
                {/* <div className="col-md-6 form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("email")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)} // Update email filter
                  />
                </div> */}

                <div className="col-md-12 form-group">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="other-stores"
                        className="form-check-input ms-0 me-2"
                        checked={otherStores}
                        onChange={(e) => setOtherStores(e.target.checked)} // Update other stores filter
                      />
                      <label
                        className="form-check-label"
                        htmlFor="other-stores"
                      >
                        <FormattedMessage id="other_stores" />
                      </label>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        // if (!stringJpRegexValidate.test(nameKana)) {
                        //   setNameKanaError("only_kana_are_allowed");
                        // } else {
                          callCustomerListReservationFilter(0);
                        // }
                      }} // Apply filters
                    >
                      <span className="text-white">
                        <i className="bi bi-search"></i>
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <ErrorMessage
                name="selectedCustomer"
                component="div"
                className="text-danger mb-2"
              />
              <div
                className="overflow-auto mt-3"
                style={{
                  height: "calc(100dvh - 414px)",
                  overflowX: "hidden",
                }}
              >
                <React.Fragment>
                  {customers?.map((customer) => {
                    const isSelected = values.selectedCustomer === customer.Id;

                    return (
                      <div key={customer.Id}>
                        <div className="customer-item">
                          <div className="label-style method-checkbox">
                            <input
                              type="checkbox"
                              name="selectedCustomerId"
                              id={String(customer.Id)}
                              value={customer.Id}
                              checked={isSelected}
                              onChange={async (e) => {
                                const checked = e.target.checked;
                                if (checked) 
                                  await formikProps.setFieldValue("selectedCustomer", customer.Id);
                                else
                                  await formikProps.setFieldValue("selectedCustomer", "-1");
                                formikProps.setFieldTouched("selectedCustomer", true, false);
                                await formikProps.validateField("selectedCustomer");
                              }}
                              className="mr-4 h-5 w-5"
                            />
                            <label htmlFor={customer?.Id} className="ms-2">
                              {customer.LastNameKana}{" "}{customer?.FirstNameKana}{" "}
                              {(customer?.FirstName || customer?.LastName) && (
                                <>
                                  <span className="text-muted">
                                    ( {customer?.LastName}{" "}{customer?.FirstName}
                                    )
                                  </span>
                                </>
                              )}
                              <br></br>
                              <small className="text-muted mt-1">
                                {customer?.Phone1}
                              </small>
                            </label>
                          </div>
                        </div>
                        <div
                          ref={downLoaderRef}
                          style={{ height: "2px" }}
                        ></div>
                      </div>
                    );
                  })}
                </React.Fragment>
                {customerReservationListState.inProgress && <p>Loading...</p>}
                <div ref={downLoaderRef} style={{ height: "2px" }}></div>
              </div>
            </div>
          </Tab.Pane>

          {/* New Customer Tab Content */}
          <Tab.Pane eventKey={1}>
            <div className="p-3">
              <div className="mb-3">
                <Label
                  className="form-label mb-0"
                  required={true}
                  label="last_name_kana"
                />
                <input
                  type="text"
                  className="form-control"
                  name="LastNameKana"
                  value={values.LastNameKana}
                  onChange={async (e) => {
                    let value = e.target.value;
                    const result = stringJpRegex.test(e.target.value)
                    // if (stringJpRegex.test(e.target.value)) {
                      await setFieldValue("LastNameKana", value);
                      formikProps.setFieldTouched("LastNameKana", true, false); // Mark as touched
                      await formikProps.validateField("LastNameKana");
                    // } else {
                      // e.preventDefault();
                      // await setFieldValue("LastNameKana", values.LastNameKana);
                    // }
                  }}
                />
                <ErrorMessage
                  name="LastNameKana"
                  component="span"
                  className="text-danger mb-2"
                />
              </div>

              <div className="mb-3">
                <Label
                  className="form-label mb-0"
                  required={true}
                  label="first_name_kana"
                />
                <input
                  type="text"
                  className="form-control"
                  name="FirstNameKana"
                  value={values.FirstNameKana}
                  onChange={async (e) => {
                    let value = e.target.value;
                    // if (stringJpRegex.test(value)) {
                      await setFieldValue("FirstNameKana", e.target.value);
                      formikProps.setFieldTouched("FirstNameKana", true, false); // Mark as touched
                      await formikProps.validateField("FirstNameKana");
                    // } else {
                      // await setFieldValue("FirstNameKana", values.FirstNameKana);
                    // }
                  }}
                />
                <ErrorMessage
                  name="FirstNameKana"
                  component="span"
                  className="text-danger mb-2"
                />
              </div>

              <div className="mb-3">
                <Label
                  className="form-label mb-0"
                  required={true}
                  label="contact_number"
                />
                <PhoneNumberInput
                  value={values.Phone}
                  type="tel"
                  className="form-control"
                  name="Phone"
                  onChange={async (phoneNumber: string) => {
                    await setFieldValue("Phone", phoneNumber); // Update Formik value
                    formikProps.setFieldTouched("Phone", true, false); // Mark as touched
                    await formikProps.validateField("Phone");
                  }}
                />
                <ErrorMessage
                  name="Phone"
                  component="span"
                  className="text-danger mb-2"
                />
              </div>

              <div className="mb-3">
                <Label
                  className="form-label mb-0"
                  required={false}
                  label="email"
                />
                <input
                  type="email"
                  className="form-control"
                  name="Email"
                  value={values.Email}
                  onChange={async (e) => {
                    await setFieldValue("Email", e.target.value); // Update Formik value
                    formikProps.setFieldTouched("Email", true, false); // Mark as touched
                    await formikProps.validateField("Email");
                  }} // Update email
                />
                <ErrorMessage
                  name="Email"
                  component="span"
                  className="text-danger mb-2"
                />
              </div>

              <div className="mb-3">
                <Label
                  className="form-label mb-0"
                  required={false}
                  label="gender"
                />
                <div className="d-flex gap-4">
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="Gender"
                      id="gender-man"
                      value="0"
                      checked={values.Gender === "0"}
                      onChange={(e) => setFieldValue("Gender", e.target.value)} // Update gender to male
                    />
                    <label className="form-check-label" htmlFor="gender-man">
                      <FormattedMessage id="male" />
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="Gender"
                      id="gender-female"
                      value="1"
                      checked={values.Gender === "1"}
                      onChange={(e) => setFieldValue("Gender", e.target.value)} // Update gender to female
                    />
                    <label className="form-check-label" htmlFor="gender-female">
                      <FormattedMessage id="female" />
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="Gender"
                      id="gender-not-selected"
                      value="2"
                      checked={values.Gender === "2"}
                      onChange={(e) => setFieldValue("Gender", e.target.value)} // Update gender to female
                    />
                    <label
                      className="form-check-label"
                      htmlFor="gender-not-selected"
                    >
                      <FormattedMessage id="not_selected" />
                    </label>
                  </div>
                </div>
                <ErrorMessage
                  name="Gender"
                  component="span"
                  className="text-danger mb-2"
                />
              </div>
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
});

export default ResetPasswordWindowPopupHOC(AuthorizeCustomerPopupHOC(observer(AddReservationFormComponent)));
